import React, { useEffect, useState } from 'react';
import useSWR from 'swr';
import Swal from 'sweetalert2';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton,
  InputAdornment, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  ambil, kirim, pesanGalat, denganCabang, jam, senin, tambahHari, tglIso, labelTgl, NAMA_HARI, URUT_HARI, ribuan, angka, rupiah,
} from './bersama';

// Tab Shift & Jadwal: daftar shift karyawan, jadwal per minggu (pengecualian per tanggal), pola mingguan & gaji pokok.

const DialogShift = ({ shift, cabang, onTutup, onTersimpan }) => {
  const [isi, setIsi] = useState({ nama: '', jamMulai: '07:00', jamSelesai: '15:00' });
  const [galat, setGalat] = useState('');
  useEffect(() => {
    setGalat('');
    setIsi(shift?.uuid ? { nama: shift.nama, jamMulai: jam(shift.jamMulai), jamSelesai: jam(shift.jamSelesai) } : { nama: '', jamMulai: '07:00', jamSelesai: '15:00' });
  }, [shift]);
  const simpan = async () => {
    try {
      const r = shift?.uuid
        ? await kirim('PUT', `/absensi/shift-karyawan/${shift.uuid}`, { ...isi, cabang: cabang || undefined })
        : await kirim('POST', '/absensi/shift-karyawan', { ...isi, cabang: cabang || undefined });
      onTersimpan(r.message);
    } catch (e) {
      setGalat(pesanGalat(e));
    }
  };
  const lewatMalam = isi.jamSelesai && isi.jamMulai && isi.jamSelesai <= isi.jamMulai;
  return (
    <Dialog open={!!shift} onClose={onTutup} maxWidth="xs" fullWidth>
      <DialogTitle>{shift?.uuid ? 'Ubah shift' : 'Shift baru'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField label="Nama shift" placeholder="mis. Pagi" value={isi.nama} onChange={(e) => setIsi({ ...isi, nama: e.target.value })} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField type="time" label="Mulai" value={isi.jamMulai} onChange={(e) => setIsi({ ...isi, jamMulai: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField type="time" label="Selesai" value={isi.jamSelesai} onChange={(e) => setIsi({ ...isi, jamSelesai: e.target.value })} InputLabelProps={{ shrink: true }} fullWidth />
        </Box>
        {lewatMalam && <Alert severity="info">Shift lewat tengah malam (selesai keesokan harinya).</Alert>}
        {galat && <Alert severity="error">{galat}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onTutup}>Batal</Button>
        <Button variant="contained" onClick={simpan}>Simpan</Button>
      </DialogActions>
    </Dialog>
  );
};

/** Pola mingguan + gaji pokok satu karyawan. */
const DialogKaryawan = ({ karyawan, shiftAktif, cabang, onTutup, onTersimpan }) => {
  const [pola, setPola] = useState({});
  const [gaji, setGaji] = useState('');
  const [tipe, setTipe] = useState('bulanan');
  const [galat, setGalat] = useState('');
  useEffect(() => {
    if (!karyawan) return;
    setGalat('');
    const p = {};
    for (const h of [0, 1, 2, 3, 4, 5, 6]) {
      const ada = karyawan.pola.find((x) => x.hari === h);
      p[h] = ada ? (ada.shiftuuid || 'libur') : '';
    }
    setPola(p);
    setGaji(karyawan.gajiPokok === null || karyawan.gajiPokok === undefined ? '' : String(karyawan.gajiPokok));
    setTipe(karyawan.tipeGaji || 'bulanan');
  }, [karyawan]);
  const simpan = async () => {
    try {
      await kirim('PUT', '/absensi/jadwal/pola', {
        cabang: cabang || undefined,
        useruuid: karyawan.uuid,
        pola: Object.entries(pola).map(([hari, shift]) => ({ hari: Number(hari), shift: shift || null })),
      });
      await kirim('PUT', '/absensi/gaji-pokok', { cabang: cabang || undefined, useruuid: karyawan.uuid, gajiPokok: gaji === '' ? null : angka(gaji), tipeGaji: tipe });
      onTersimpan('Pola mingguan & gaji pokok disimpan');
    } catch (e) {
      setGalat(pesanGalat(e));
    }
  };
  const isiSemua = (nilai) => setPola(Object.fromEntries([0, 1, 2, 3, 4, 5, 6].map((h) => [h, h === 0 ? 'libur' : nilai])));
  return (
    <Dialog open={!!karyawan} onClose={onTutup} maxWidth="sm" fullWidth>
      <DialogTitle>{karyawan?.nama}: pola mingguan & gaji pokok</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Pola berlaku setiap minggu. Perubahan untuk tanggal tertentu diatur di tabel jadwal. Hari tanpa jadwal tidak dihitung Alfa.
        </Typography>
        {shiftAktif.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {shiftAktif.map((s) => <Button key={s.uuid} size="small" variant="outlined" onClick={() => isiSemua(s.uuid)}>Sen–Sab {s.nama}, Minggu libur</Button>)}
          </Box>
        )}
        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          {URUT_HARI.map((h) => (
            <Grid item xs={6} sm={4} key={h}>
              <TextField fullWidth size="small" select label={NAMA_HARI[h]} value={pola[h] ?? ''} onChange={(e) => setPola({ ...pola, [h]: e.target.value })}>
                <MenuItem value=""><em>Tanpa jadwal</em></MenuItem>
                {shiftAktif.map((s) => <MenuItem key={s.uuid} value={s.uuid}>{s.nama} ({jam(s.jamMulai)}–{jam(s.jamSelesai)})</MenuItem>)}
                <MenuItem value="libur">Libur</MenuItem>
              </TextField>
            </Grid>
          ))}
        </Grid>
        <Typography fontWeight={700} sx={{ mb: 1 }}>Gaji pokok</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField select size="small" label="Tipe" value={tipe} onChange={(e) => setTipe(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value="bulanan">Bulanan</MenuItem>
            <MenuItem value="harian">Harian</MenuItem>
          </TextField>
          <TextField size="small" fullWidth label={tipe === 'harian' ? 'Gaji per hari masuk' : 'Gaji per bulan'} value={ribuan(gaji)} onChange={(e) => setGaji(String(angka(e.target.value) || ''))}
            InputProps={{ startAdornment: <InputAdornment position="start">Rp</InputAdornment> }} />
        </Box>
        {galat && <Alert severity="error" sx={{ mt: 2 }}>{galat}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onTutup}>Batal</Button>
        <Button variant="contained" onClick={simpan}>Simpan</Button>
      </DialogActions>
    </Dialog>
  );
};

const TabShiftJadwal = ({ shift, cabang, muatUlangPengaturan, onPesan }) => {
  const [mingguMulai, setMingguMulai] = useState(() => senin(tglIso(new Date())));
  const [ubahShift, setUbahShift] = useState(null);
  const [ubahKaryawan, setUbahKaryawan] = useState(null);
  const sampai = tambahHari(mingguMulai, 6);
  const { data, error, mutate } = useSWR(denganCabang('/absensi/jadwal', cabang, { dari: mingguMulai, sampai }), ambil);
  const karyawan = data?.data || [];
  const shiftAktif = (shift || []).filter((s) => s.aktif);
  const tanggalan = [0, 1, 2, 3, 4, 5, 6].map((i) => tambahHari(mingguMulai, i));

  const hapusShift = async (s) => {
    const ok = await Swal.fire({ title: `Hapus shift ${s.nama}?`, text: 'Bila dipakai di jadwal, shift dinonaktifkan saja.', icon: 'warning', showCancelButton: true, confirmButtonText: 'Hapus', cancelButtonText: 'Batal' });
    if (!ok.isConfirmed) return;
    try {
      const r = await kirim('DELETE', `/absensi/shift-karyawan/${s.uuid}${cabang ? `?cabang=${cabang}` : ''}`);
      onPesan(r.message); muatUlangPengaturan(); mutate();
    } catch (e) {
      Swal.fire('Gagal', pesanGalat(e), 'error');
    }
  };
  const aktifkan = async (s) => {
    try {
      await kirim('PUT', `/absensi/shift-karyawan/${s.uuid}`, { aktif: true, cabang: cabang || undefined });
      onPesan('Shift diaktifkan'); muatUlangPengaturan();
    } catch (e) {
      Swal.fire('Gagal', pesanGalat(e), 'error');
    }
  };
  const ubahSel = async (k, tanggal, nilai) => {
    try {
      await kirim('PUT', '/absensi/jadwal/tanggal', { cabang: cabang || undefined, useruuid: k.uuid, tanggal, shift: nilai || null });
      mutate();
    } catch (e) {
      Swal.fire('Gagal', pesanGalat(e), 'error');
    }
  };

  return (
    <Box>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography fontWeight={700} sx={{ flex: 1 }}>Shift karyawan</Typography>
            <Button variant="contained" size="small" onClick={() => setUbahShift({})}>Shift baru</Button>
          </Box>
          {!(shift || []).length && <Typography color="text.secondary">Belum ada shift. Buat dulu, mis. Pagi 07.00–15.00.</Typography>}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {(shift || []).map((s) => (
              <Chip key={s.uuid} variant={s.aktif ? 'filled' : 'outlined'} color={s.aktif ? 'primary' : 'default'}
                label={`${s.nama} ${jam(s.jamMulai)}–${jam(s.jamSelesai)}${s.aktif ? '' : ' (nonaktif)'}`}
                onClick={() => (s.aktif ? setUbahShift(s) : aktifkan(s))} onDelete={s.aktif ? () => hapusShift(s) : undefined} />
            ))}
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography fontWeight={700} sx={{ flex: 1 }}>Jadwal</Typography>
            <IconButton size="small" onClick={() => setMingguMulai(tambahHari(mingguMulai, -7))}><ChevronLeft /></IconButton>
            <Typography variant="body2">{labelTgl(mingguMulai, { day: 'numeric', month: 'short' })} – {labelTgl(sampai, { day: 'numeric', month: 'short', year: 'numeric' })}</Typography>
            <IconButton size="small" onClick={() => setMingguMulai(tambahHari(mingguMulai, 7))}><ChevronRight /></IconButton>
            <Button size="small" onClick={() => setMingguMulai(senin(tglIso(new Date())))}>Minggu ini</Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Klik nama karyawan untuk mengatur pola mingguan & gaji pokok. Ubah kotak tanggal untuk pengecualian (tukar shift, libur) di tanggal itu saja.
          </Typography>
          {error && <Alert severity="error">{pesanGalat(error)}</Alert>}
          {data && !karyawan.length && <Alert severity="info">Belum ada akun karyawan di cabang ini (Karyawan › Data User, peran karyawan).</Alert>}
          {karyawan.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Karyawan</TableCell>
                    {tanggalan.map((t) => <TableCell key={t} sx={{ minWidth: 130 }}>{labelTgl(t)}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {karyawan.map((k) => (
                    <TableRow key={k.uuid}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Button size="small" onClick={() => setUbahKaryawan(k)} sx={{ textTransform: 'none', p: 0, minWidth: 0 }}>{k.nama}</Button>
                        <Typography variant="caption" color="text.secondary" component="div">
                          {k.gajiPokok ? `${rupiah(k.gajiPokok)} / ${k.tipeGaji === 'harian' ? 'hari' : 'bulan'}` : 'Gaji pokok belum diisi'}
                        </Typography>
                      </TableCell>
                      {tanggalan.map((t) => {
                        const h = k.hari.find((x) => x.tanggal === t) || {};
                        const khusus = k.khusus.find((x) => x.tanggal === t);
                        const nilai = khusus ? (khusus.shiftuuid || 'libur') : '';
                        const ikut = h.jadwalLibur ? 'Libur' : h.shift ? h.shift.nama : 'tanpa jadwal';
                        return (
                          <TableCell key={t} sx={{ verticalAlign: 'top' }}>
                            <TextField select size="small" fullWidth value={nilai} onChange={(e) => ubahSel(k, t, e.target.value)}
                              sx={{ '& .MuiInputBase-root': { fontWeight: khusus ? 700 : 400 } }}>
                              <MenuItem value="">Pola ({khusus ? 'kembalikan' : ikut})</MenuItem>
                              {shiftAktif.map((s) => <MenuItem key={s.uuid} value={s.uuid}>{s.nama}</MenuItem>)}
                              <MenuItem value="libur">Libur</MenuItem>
                            </TextField>
                            {h.libur && <Chip size="small" sx={{ mt: 0.5 }} color={h.libur.status === 'buka' ? 'default' : 'secondary'}
                              label={h.libur.jenis === 'cabang' ? h.libur.nama : `${h.libur.nama}${h.libur.status === 'buka' ? ' (buka)' : ''}`} />}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <DialogShift shift={ubahShift} cabang={cabang} onTutup={() => setUbahShift(null)}
        onTersimpan={(m) => { setUbahShift(null); onPesan(m); muatUlangPengaturan(); mutate(); }} />
      <DialogKaryawan karyawan={ubahKaryawan} shiftAktif={shiftAktif} cabang={cabang} onTutup={() => setUbahKaryawan(null)}
        onTersimpan={(m) => { setUbahKaryawan(null); onPesan(m); mutate(); }} />
    </Box>
  );
};

export default TabShiftJadwal;
